import { useMemo } from "react";

import {
  APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LINKS,
  useGetAppointmentTypesQuery
} from "services/appointment-types";

export type LinkedAppointmentType = {
  id: number;
  name: string;
  color?: string | null;
  is_active?: boolean;
};

type AppointmentTypeLinkRow = {
  id: number;
  name: string;
  color?: string | null;
  is_active?: boolean;
  ambient_scribe_template_id?: number | null;
};

const QUERY_PARAMS = { query: APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LINKS };

export const EMPTY_APPOINTMENT_TYPE_LINKS: LinkedAppointmentType[] = [];

/**
 * "Which appointment types use this scribe template?" — answered from the AUTHORITATIVE
 * direction (SN-19067).
 *
 * Two FKs describe this relationship and they routinely disagree:
 *
 *   - `AppointmentType.ambient_scribe_template_id` — the only one scribe-time resolution
 *     reads, and the only one the Suno UI ever writes (the template modal PATCHes it via
 *     `useSetAppointmentTypeScribeTemplateMutation`). This is what we group here.
 *   - `AmbientScribeTemplate.appointment_type` — the reverse FK the API can nest under
 *     `?query=`. It is read-only and only ever populated by backend seeding, so it is
 *     `null` for every template created through the UI. Rendering a column from it would
 *     show "—" for a template the user had just linked, while the delete dialog for the
 *     same row correctly names the appointment type.
 *
 * Deliberately no `is_active` filter: an inactive appointment type holds the same live
 * reference, so hiding it would under-report which types a template change affects.
 */
export function groupAppointmentTypesByTemplateId(
  appointmentTypes: readonly AppointmentTypeLinkRow[] | undefined
): Map<number, LinkedAppointmentType[]> {
  const map = new Map<number, LinkedAppointmentType[]>();
  for (const appointmentType of appointmentTypes ?? []) {
    const templateId = appointmentType.ambient_scribe_template_id;
    if (templateId == null) continue;
    const entry: LinkedAppointmentType = {
      id: appointmentType.id,
      name: appointmentType.name,
      color: appointmentType.color,
      is_active: appointmentType.is_active
    };
    const existing = map.get(templateId);
    if (existing) existing.push(entry);
    else map.set(templateId, [entry]);
  }
  return map;
}

/**
 * Fetch-and-group wrapper for callers that don't already hold the appointment-types list.
 *
 * The scribe-settings list uses `groupAppointmentTypesByTemplateId` directly instead — it
 * already fetches this list unconditionally for the delete-confirmation dialog, and that
 * fetch must NOT become flag-conditional.
 *
 * @param enabled Skips the fetch entirely when false (the feature flag is off).
 */
export default function useScribeTemplateAppointmentTypes(enabled: boolean) {
  const { data: appointmentTypes, isError } = useGetAppointmentTypesQuery(
    QUERY_PARAMS,
    { skip: !enabled }
  );

  const byTemplateId = useMemo(
    () => groupAppointmentTypesByTemplateId(appointmentTypes),
    [appointmentTypes]
  );

  return { byTemplateId, isError };
}
